/**
 * Subscription Configuration for Rennzone Marketplace
 * ONE LISTING = ONE SUBSCRIPTION MODEL
 *
 * Maps listing categories and ad levels to Stripe price IDs
 * Each listing gets its own monthly recurring subscription
 *
 * This configuration enables 14 different subscription tiers:
 * - 6 single-tier categories (Parts, Books, Watches, etc.)
 * - 8 multi-tier categories (Road Cars & Race Cars with 4 levels each)
 */

// Get Stripe price IDs from environment variables
const getStripePriceIds = () => ({
  // Single-tier categories
  NEW_USED_PARTS: process.env.STRIPE_PRICE_NEW_USED_PARTS,
  BOOKS_ART_CLOTHING: process.env.STRIPE_PRICE_BOOKS_ART_CLOTHING,
  WATCHES_MEMORABILIA: process.env.STRIPE_PRICE_WATCHES_MEMORABILIA,
  MODELS_TOYS: process.env.STRIPE_PRICE_MODELS_TOYS,

  // Multi-tier categories (same prices for Road Cars and Race Cars)
  CARS_STANDARD: process.env.STRIPE_PRICE_CARS_STANDARD,
  CARS_FEATURED: process.env.STRIPE_PRICE_CARS_FEATURED,
  CARS_PREMIUM: process.env.STRIPE_PRICE_CARS_PREMIUM,
  CARS_PREMIUM_PRO: process.env.STRIPE_PRICE_CARS_PREMIUM_PRO,
});

// Subscription tier definitions with pricing information
export const SUBSCRIPTION_TIERS = {
  // Single-tier categories — all free to list
  'parts-listing': {
    category: 'parts-listing',
    adLevel: null,
    priceKey: 'NEW_USED_PARTS',
    displayName: 'New & Used Parts Listing',
    price: 'Free to list',
    isFree: true,
    description: 'Free listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
  },

  'porsche-books-art-for-sale': {
    category: 'porsche-books-art-for-sale',
    adLevel: null,
    priceKey: 'BOOKS_ART_CLOTHING',
    displayName: 'Books, Art & Clothing Listing',
    price: 'Free to list',
    isFree: true,
    description: 'Free listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
  },

  'clothing': {
    category: 'clothing',
    adLevel: null,
    priceKey: 'BOOKS_ART_CLOTHING',
    displayName: 'Books, Art & Clothing Listing',
    price: 'Free to list',
    isFree: true,
    description: 'Free listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
  },

  'watches': {
    category: 'watches',
    adLevel: null,
    priceKey: 'WATCHES_MEMORABILIA',
    displayName: 'Watches & Memorabilia Listing',
    price: 'Free to list',
    isFree: true,
    description: 'Free listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
  },

  'porsche-memorabilia-for-sale': {
    category: 'porsche-memorabilia-for-sale',
    adLevel: null,
    priceKey: 'WATCHES_MEMORABILIA',
    displayName: 'Watches & Memorabilia Listing',
    price: 'Free to list',
    isFree: true,
    description: 'Free listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
  },

  'porsche-models-for-sale': {
    category: 'porsche-models-for-sale',
    adLevel: null,
    priceKey: 'MODELS_TOYS',
    displayName: 'Models & Toys Listing',
    price: 'Free to list',
    isFree: true,
    description: 'Free listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
  },

  // Multi-tier categories - Road Cars
  'cars-standard-listing-standard': {
    category: 'cars-standard-listing',
    adLevel: 'standard_level',
    priceKey: 'CARS_STANDARD',
    displayName: 'Cars Standard',
    price: 'Free to list',
    isFree: true,
    description: 'Free listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
  },

  'cars-standard-listing-featured': {
    category: 'cars-standard-listing',
    adLevel: 'featured_level',
    priceKey: 'CARS_FEATURED',
    displayName: 'Cars Featured',
    price: '£24.95',
    isFree: false,
    description: 'Monthly subscription per listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Walkaround Video Uploads',
      'Track Listing Analytics',
      'Stand-Out Search Highlight',
      'Prime Homepage Placement',
    ],
  },

  'cars-standard-listing-premium': {
    category: 'cars-standard-listing',
    adLevel: 'premium_level',
    priceKey: 'CARS_PREMIUM',
    displayName: 'Cars Premium',
    price: '£79.95',
    isFree: false,
    description: 'Monthly subscription per listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Walkaround Video Uploads',
      'Track Listing Analytics',
      'Stand-Out Search Highlight',
      'VIP Homepage Priority',
      'Rennzone Social Media Boost',
    ],
  },

  'cars-standard-listing-premium-pro': {
    category: 'cars-standard-listing',
    adLevel: 'premium_pro_level',
    priceKey: 'CARS_PREMIUM_PRO',
    displayName: 'Cars Premium Pro',
    price: '£299.95',
    isFree: false,
    description: 'Monthly subscription per listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Walkaround Video Uploads',
      'Track Listing Analytics',
      'Stand-Out Search Highlight',
      'VIP Homepage Priority',
      'Rennzone Social Media Boost',
      'Targeted Google Ads Campaign',
    ],
  },

  // Multi-tier categories - Race Cars (same pricing structure as Road Cars)
  'porsche-race-cars-for-sale-standard': {
    category: 'porsche-race-cars-for-sale',
    adLevel: 'standard_level',
    priceKey: 'CARS_STANDARD',
    displayName: 'Cars Standard',
    price: 'Free to list',
    isFree: true,
    description: 'Free listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Track Listing Analytics',
    ],
  },

  'porsche-race-cars-for-sale-featured': {
    category: 'porsche-race-cars-for-sale',
    adLevel: 'featured_level',
    priceKey: 'CARS_FEATURED',
    displayName: 'Cars Featured',
    price: '£24.95',
    isFree: false,
    description: 'Monthly subscription per listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Walkaround Video Uploads',
      'Track Listing Analytics',
      'Stand-Out Search Highlight',
      'Prime Homepage Placement',
    ],
  },

  'porsche-race-cars-for-sale-premium': {
    category: 'porsche-race-cars-for-sale',
    adLevel: 'premium_level',
    priceKey: 'CARS_PREMIUM',
    displayName: 'Cars Premium',
    price: '£79.95',
    isFree: false,
    description: 'Monthly subscription per listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Walkaround Video Uploads',
      'Track Listing Analytics',
      'Stand-Out Search Highlight',
      'VIP Homepage Priority',
      'Rennzone Social Media Boost',
    ],
  },

  'porsche-race-cars-for-sale-premium-pro': {
    category: 'porsche-race-cars-for-sale',
    adLevel: 'premium_pro_level',
    priceKey: 'CARS_PREMIUM_PRO',
    displayName: 'Cars Premium Pro',
    price: '£299.95',
    isFree: false,
    description: 'Monthly subscription per listing',
    features: [
      'Unlimited High-Res Photos',
      'Secure Buyer Messaging',
      'Walkaround Video Uploads',
      'Track Listing Analytics',
      'Stand-Out Search Highlight',
      'VIP Homepage Priority',
      'Rennzone Social Media Boost',
      'Targeted Google Ads Campaign',
    ],
  },
};

/**
 * Get subscription tier key from category and ad level
 * @param {string} category - Listing category ID
 * @param {string|null} adLevel - Ad level (null for single-tier categories)
 * @returns {string|null} - Subscription tier key or null if not found
 */
export const getSubscriptionTierKey = (category, adLevel) => {
  if (!category) return null;

  // Handle single-tier categories (no ad level)
  if (!adLevel || adLevel === '') {
    // Direct match on category
    const singleTierKey = Object.keys(SUBSCRIPTION_TIERS).find(key => {
      const tier = SUBSCRIPTION_TIERS[key];
      return tier.category === category && tier.adLevel === null;
    });
    return singleTierKey || null;
  }

  // Handle multi-tier categories (with ad level)
  // Build key as: category-adLevelWithoutSuffix
  // e.g., "cars-standard-listing" + "standard_level" = "cars-standard-listing-standard"
  // Note: Replace underscores with hyphens to match tier key format
  const adLevelShort = adLevel.replace('_level', '').replace(/_/g, '-');
  const multiTierKey = `${category}-${adLevelShort}`;

  return SUBSCRIPTION_TIERS[multiTierKey] ? multiTierKey : null;
};

/**
 * Get Stripe price ID for a subscription tier
 * @param {string} tierKey - Subscription tier key
 * @returns {string|null} - Stripe price ID or null if not found
 */
export const getStripePriceId = (tierKey) => {
  const tier = SUBSCRIPTION_TIERS[tierKey];
  if (!tier) return null;

  const priceIds = getStripePriceIds();
  return priceIds[tier.priceKey] || null;
};

/**
 * Get subscription tier information
 * @param {string} category - Listing category ID
 * @param {string|null} adLevel - Ad level
 * @returns {object|null} - Tier information or null if not found
 */
export const getSubscriptionTier = (category, adLevel) => {
  const tierKey = getSubscriptionTierKey(category, adLevel);
  if (!tierKey) return null;

  const tier = SUBSCRIPTION_TIERS[tierKey];
  const priceId = getStripePriceId(tierKey);

  return {
    ...tier,
    tierKey,
    priceId
  };
};

/**
 * Get all available subscription tiers
 * @returns {array} - Array of all tiers with price IDs
 */
export const getAllSubscriptionTiers = () => {
  const priceIds = getStripePriceIds();

  return Object.keys(SUBSCRIPTION_TIERS).map(tierKey => {
    const tier = SUBSCRIPTION_TIERS[tierKey];
    return {
      ...tier,
      tierKey,
      priceId: priceIds[tier.priceKey]
    };
  });
};

/**
 * Validate if category and ad level combination is valid for subscriptions
 * @param {string} category - Listing category ID
 * @param {string|null} adLevel - Ad level
 * @returns {boolean} - True if valid subscription tier exists
 */
export const isValidSubscriptionTier = (category, adLevel) => {
  if (!category) return false;

  const tierKey = getSubscriptionTierKey(category, adLevel);
  return tierKey !== null;
};

/**
 * Get subscription tiers by category
 * @param {string} category - Listing category ID
 * @returns {array} - Array of tiers for the category
 */
export const getSubscriptionTiersByCategory = (category) => {
  return Object.keys(SUBSCRIPTION_TIERS)
    .filter(tierKey => SUBSCRIPTION_TIERS[tierKey].category === category)
    .map(tierKey => ({
      ...SUBSCRIPTION_TIERS[tierKey],
      tierKey,
      priceId: getStripePriceId(tierKey)
    }));
};

/**
 * Check if category has multiple tiers (ad levels)
 * @param {string} category - Listing category ID
 * @returns {boolean} - True if category has multiple tiers
 */
export const hasMultipleTiers = (category) => {
  const tiers = getSubscriptionTiersByCategory(category);
  return tiers.length > 1;
};

/**
 * Validate subscription configuration
 * @returns {object} - Validation result with missing price IDs
 */
export const validateSubscriptionConfig = () => {
  const priceIds = getStripePriceIds();
  const missing = [];
  const configured = [];

  Object.keys(SUBSCRIPTION_TIERS).forEach(tierKey => {
    const tier = SUBSCRIPTION_TIERS[tierKey];
    const priceId = priceIds[tier.priceKey];

    if (!priceId) {
      missing.push({
        tierKey,
        priceKey: tier.priceKey,
        displayName: tier.displayName,
        environmentVariable: `STRIPE_PRICE_${tier.priceKey}`
      });
    } else {
      configured.push({
        tierKey,
        priceKey: tier.priceKey,
        displayName: tier.displayName,
        priceId: priceId
      });
    }
  });

  return {
    isValid: missing.length === 0,
    totalTiers: Object.keys(SUBSCRIPTION_TIERS).length,
    configuredCount: configured.length,
    missingCount: missing.length,
    missing,
    configured
  };
};

/**
 * Get subscription tier with environment-aware fallbacks
 * @param {string} category - Listing category ID
 * @param {string|null} adLevel - Ad level
 * @returns {object|null} - Tier information with fallback handling
 */
export const getSubscriptionTierSafe = (category, adLevel) => {
  const tier = getSubscriptionTier(category, adLevel);

  if (!tier) {
    return null;
  }

  // Check if price ID is configured
  if (!tier.priceId) {
    console.warn(`⚠️ Stripe price ID not configured for tier: ${tier.tierKey}`);
    console.warn(`   Set environment variable: STRIPE_PRICE_${tier.priceKey}`);

    return {
      ...tier,
      isConfigured: false,
      error: 'Price ID not configured',
      environmentVariable: `STRIPE_PRICE_${tier.priceKey}`
    };
  }

  return {
    ...tier,
    isConfigured: true
  };
};

/**
 * Log subscription configuration summary
 */
export const logSubscriptionConfig = () => {
  const validation = validateSubscriptionConfig();

  console.log('\n💰 Subscription Configuration Summary');
  console.log('=====================================');
  console.log(`Total Tiers: ${validation.totalTiers}`);
  console.log(`Configured: ${validation.configuredCount} ✅`);
  console.log(`Missing: ${validation.missingCount} ${validation.missingCount > 0 ? '❌' : '✅'}`);

  if (validation.missing.length > 0) {
    console.log('\n❌ Missing Stripe Price IDs:');
    validation.missing.forEach(tier => {
      console.log(`• ${tier.displayName}`);
      console.log(`  Environment Variable: ${tier.environmentVariable}`);
      console.log(`  Price Key: ${tier.priceKey}`);
    });

    console.log('\n💡 To fix missing price IDs:');
    console.log('1. Create products and prices in Stripe Dashboard');
    console.log('2. Add price IDs to environment variables');
    console.log('3. Restart the server');
  }

  if (validation.configured.length > 0 && validation.missing.length === 0) {
    console.log('\n✅ All subscription tiers properly configured!');
  }

  console.log('=====================================\n');

  return validation;
};

export default {
  SUBSCRIPTION_TIERS,
  getSubscriptionTierKey,
  getStripePriceId,
  getSubscriptionTier,
  getSubscriptionTierSafe,
  getAllSubscriptionTiers,
  isValidSubscriptionTier,
  getSubscriptionTiersByCategory,
  hasMultipleTiers,
  validateSubscriptionConfig,
  logSubscriptionConfig
};
